import { Button } from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system/permissions'
import type { FreightQuoteOption } from '../api/types'

export interface CarrierComparisonTableProps {
  options: FreightQuoteOption[]
  onTender: (option: FreightQuoteOption) => void
  tenderDisabled?: boolean
}

/**
 * Logistics overhaul Phase 2b — the quote-options table gains a Tender
 * action per row. Only rendered for roles holding tender authority
 * (admin, operations_logistics — see app/rbac.py can_tender_freight);
 * everyone else sees the same read-only comparison Phase 2a shipped.
 */
export function CarrierComparisonTable({
  options,
  onTender,
  tenderDisabled,
}: CarrierComparisonTableProps) {
  const canTender = useHasAnyPermission(['admin', 'operations_logistics'])

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-role-metadata text-tertiary">
          <th className="pb-1.5 font-normal">Carrier</th>
          <th className="pb-1.5 font-normal">Service</th>
          <th className="pb-1.5 font-normal">Transit</th>
          <th className="pb-1.5 text-right font-normal">Total</th>
          {canTender ? <th className="pb-1.5 pr-0 text-right font-normal" /> : null}
        </tr>
      </thead>
      <tbody>
        {options.map((q, i) => (
          <tr key={q.rate_id ?? i} className="border-t border-border-subtle">
            <td className="py-1.5">
              {q.carrier_name ?? '—'}
              {q.carrier_scac ? <span className="ml-1 text-tertiary">({q.carrier_scac})</span> : null}
            </td>
            <td className="py-1.5">{q.service_level ?? '—'}</td>
            <td className="py-1.5">{q.transit_days != null ? `${q.transit_days}d` : '—'}</td>
            <td className="py-1.5 text-right font-medium">
              {q.currency} {q.total.toFixed(2)}
            </td>
            {canTender ? (
              <td className="py-1.5 pr-0 text-right">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={tenderDisabled}
                  onClick={() => onTender(q)}
                >
                  Tender
                </Button>
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
